/**
 * Sconti di listino per frequenza di fatturazione.
 *
 * Prima di questo modulo il CRM sapeva solo dividere il canone in proporzione
 * (trimestrale = mensile × 3). Qui il catalogo può dire "l'annuale costa il 15%
 * in meno" oppure "l'annuale costa 1.190 €", e la riga dell'offerta applica la
 * regola da sola.
 *
 * I dati vivono in `services.billing_prices` e `service_addons.billing_prices`
 * (jsonb, stessa forma per entrambi), con la chiave uguale al numero di mesi
 * del periodo — gli stessi valori già usati da `metadata.billing_interval_months`:
 *
 *   { "6": { "mode": "percent", "value": 10 }, "12": { "mode": "fixed", "price": 1190 } }
 *
 * Tutto è opzionale: senza regole il comportamento resta quello di oggi, quindi
 * il modulo è sicuro anche dove la migration non è ancora stata applicata.
 */

export type BillingPriceRule =
  | { mode: 'percent'; value: number }
  | { mode: 'fixed'; price: number };

export type BillingPrices = Record<string, BillingPriceRule>;

/** Periodi di fatturazione supportati, in mesi. */
export const BILLING_PERIODS = [1, 2, 3, 4, 6, 12] as const;

export const BILLING_PERIOD_LABEL: Record<number, string> = {
  1: 'Mensile',
  2: 'Bimestrale',
  3: 'Trimestrale',
  4: 'Quadrimestrale',
  6: 'Semestrale',
  12: 'Annuale',
};

/** Suffisso da mettere accanto al prezzo: "/mese", "/trimestre"… */
export const BILLING_PERIOD_SUFFIX: Record<number, string> = {
  1: '/mese',
  2: '/bimestre',
  3: '/trimestre',
  4: '/quadrimestre',
  6: '/semestre',
  12: '/anno',
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Legge in modo tollerante il jsonb, che può arrivare come stringa o malformato. */
export function parseBillingPrices(raw: unknown): BillingPrices {
  if (!raw) return {};
  let obj: any = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof obj !== 'object' || Array.isArray(obj)) return {};
  const out: BillingPrices = {};
  for (const [key, rule] of Object.entries(obj as Record<string, any>)) {
    const months = Number(key);
    if (!Number.isFinite(months) || months <= 0) continue;
    if (rule?.mode === 'percent' && Number.isFinite(Number(rule.value))) {
      out[String(months)] = { mode: 'percent', value: Number(rule.value) };
    } else if (rule?.mode === 'fixed' && Number.isFinite(Number(rule.price))) {
      out[String(months)] = { mode: 'fixed', price: Number(rule.price) };
    }
  }
  return out;
}

/**
 * Canone mensile implicito di un elemento del catalogo. `list_price` significa
 * cose diverse a seconda del tipo: per un servizio annuale è il prezzo di un
 * anno, per un mensile quello di un mese. Qui si normalizza a mese, che è
 * l'unità su cui ragionano tutti i calcoli.
 */
export function monthlyBaseOf(price: number, serviceType?: string | null): number {
  const p = Number.isFinite(price) ? price : 0;
  return serviceType === 'annual' ? p / 12 : p;
}

/** Prezzo del periodo senza alcuno sconto: canone mensile × mesi. */
export function fullPriceForPeriod(monthlyBase: number, months: number): number {
  return round2(monthlyBase * months);
}

/**
 * Prezzo di listino del periodo, applicata la regola configurata a catalogo.
 * Senza regola per quel periodo si ricade sul prezzo pieno, cioè su come si
 * comporta il CRM oggi.
 */
export function listPriceForPeriod(monthlyBase: number, rules: BillingPrices, months: number): number {
  const rule = rules[String(months)];
  if (rule?.mode === 'fixed') return round2(rule.price);
  const full = fullPriceForPeriod(monthlyBase, months);
  if (rule?.mode === 'percent') return round2(full * (1 - rule.value / 100));
  return full;
}

/** Sconto percentuale che la regola produce su quel periodo (0 se nessuno). */
export function discountPercentForPeriod(monthlyBase: number, rules: BillingPrices, months: number): number {
  const full = fullPriceForPeriod(monthlyBase, months);
  if (full <= 0) return 0;
  const list = listPriceForPeriod(monthlyBase, rules, months);
  const pct = (1 - list / full) * 100;
  return pct > 0.5 ? Math.round(pct) : 0;
}
